/**
 * FirstImpressionCard — instant delight moment right after a resume is
 * saved: Resmo's detected role, top 3 skills and a one-line hook.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, Download, Copy, Check } from "lucide-react";
import { api, API } from "../lib/api";
import JobMatchesPanel from "./JobMatchesPanel";

export default function FirstImpressionCard({ className = "" }) {
  const [imp, setImp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(imp?.share_caption || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard blocked */ }
  };

  useEffect(() => {
    api.post("/resume/first-impression")
      .then((r) => setImp(r.data))
      .catch(() => setImp(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className={`p-4 rounded-lg animate-pulse ${className}`}
           style={{ border: "2px dashed #FFC145", background: "#FFFBEB" }}
           data-testid="first-impression-loading">
        <div className="flex items-center gap-2 font-mono-rm text-[11px] uppercase tracking-widest font-bold"
             style={{ color: "#B45309" }}>
          <Sparkles size={13} /> Resmo is taking a first look…
        </div>
      </div>
    );
  }
  if (!imp) return null;
  return (
    <>
    <div className={`p-4 rounded-lg ${className}`}
         style={{ border: "2px solid #0F1B2D", background: "#FFFBEB", boxShadow: "4px 4px 0 0 #FFC145" }}
         data-testid="first-impression-card">
      <div className="flex items-center gap-1.5 font-mono-rm text-[10px] uppercase tracking-widest font-bold"
           style={{ color: "#FF6A00" }}>
        <Sparkles size={12} /> · First impression
      </div>
      <div className="font-display text-lg uppercase leading-tight mt-1" style={{ color: "#0F1B2D" }}
           data-testid="first-impression-role">
        Looks like a {imp.detected_role}
      </div>
      {(imp.top_skills || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="first-impression-skills">
          {imp.top_skills.map((s) => (
            <span key={s} className="px-2 py-0.5 text-[11px] font-bold rounded-full"
                  style={{ background: "white", border: "2px solid #0F1B2D", color: "#0F1B2D" }}>
              {s}
            </span>
          ))}
        </div>
      )}
      {imp.hook && (
        <p className="text-xs italic mt-2 leading-snug" style={{ color: "#475569" }} data-testid="first-impression-hook">
          "{imp.hook}"
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3" style={{ borderTop: "1px dashed #E2C989" }}>
        <a
          href={`${API}/resume/first-impression/card.png`}
          download="resmo-first-impression.png"
          target="_blank"
          rel="noreferrer"
          className="rm-btn text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1"
          style={{ background: "white", color: "#0F1B2D", textDecoration: "none" }}
          title="Download a share-ready image for LinkedIn"
          data-testid="first-impression-download"
        >
          <Download size={11} /> Share card
        </a>
        <button
          type="button"
          onClick={copyCaption}
          className="rm-btn rm-btn-ghost text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1"
          data-testid="first-impression-copy-caption"
        >
          {copied ? <><Check size={11} /> Copied!</> : <><Copy size={11} /> Copy caption</>}
        </button>
        <span className="font-mono-rm text-[9px] uppercase tracking-widest" style={{ color: "#B45309" }}>
          Post it — tag your glow-up
        </span>
      </div>
    </div>
    <JobMatchesPanel className="mt-3" limit={3} />
    </>
  );
}
